import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useCancelableTimeout } from '../useCancelableTimeout';
import { LoggerContext } from './loggerContext';

// Does any logger field currently hold the keyboard? (TM-18a)
//
// One consumer today: the rest-end resume in useRestAdvance, which must NOT
// yank the focus out of a field the athlete is typing into. Antenore's rule for
// the transition is "wandering is temporary, the rest ending brings you back to
// the work" — but tearing a caret out of a half-typed weight is the new bug we
// would be buying, so the resume waits for the field to let go.
//
// Why a signal and not a lookup: RN's TextInput.State.currentlyFocusedInput()
// is imperative, unobservable, and its react-native-web behaviour is not the
// native one — a hook that has to re-run when the answer changes cannot read it,
// and packages/ui is built RN-portable by default. Keyboard.addListener would
// have been worse than absent: it never fires for a hardware keyboard, i.e. it
// fails silently on exactly the surface the lift targets. The fields already
// announce focus and blur; this only remembers what they said.
//
// It rides the LoggerContext bag rather than a context of its own: the bag is
// already the way the logger subtree reaches state owned above the <Modal>, and
// this state has to live up there for the same LG-13 reason the cursor does —
// the hook that reads it (useRestAdvance) is instantiated in the LoggerScreen
// body.

export type FieldFocusSignal = {
  /** True while at least one logger field holds focus. */
  anyFieldFocused: boolean;
  onFieldFocus: (fieldId: string) => void;
  onFieldBlur: (fieldId: string) => void;
  /** LG-78 — the app itself declaring the keyboard is down.
   *
   *  Every other way this signal falls is an event a FIELD sends. That is one
   *  event too few: `dismissSoftKeyboard()` is the app ASSERTING the keyboard
   *  is closed, and RN answers it by blurring `currentlyFocusedInput()` — so
   *  when that is already null (the plate-calculator Modal has taken over, or
   *  the field was unmounted under a card swap) nothing is blurred and NO blur
   *  event is delivered. The signal then reads "someone is typing" with no
   *  keyboard on screen and nobody typing, and because the rest-end resume
   *  waits for a blur "never by the clock", the focus advance is stranded for
   *  good rather than late.
   *
   *  ⇒ The one performing the dismissal writes the fact, instead of waiting to
   *  be told by an event that may never come — the same lesson as the QA
   *  stamps ([[feedback_a_control_nobody_notices_collects_nothing]]).
   *
   *  ⛔ Not a weakening of TM-18b. It is called on the COMMIT gesture, before
   *  the hand-over window opens; a field the athlete takes afterwards raises
   *  the signal again on its own and their hand still wins. */
  releaseAll: () => void;
};

// LG-72 — how long a release waits for the field that is TAKING the keyboard.
//
// The Set below survives `focus-B → blur-A`. It does not survive the opposite
// order: `blur-A` empties it and the signal says "nobody is holding the
// keyboard" for as long as it takes `focus-B` to arrive — while the keyboard
// has not moved a pixel. That dip is read as an answer downstream, and
// `resumeWaiting` re-binds the panel to the next set under the athlete's
// cursor: the LG-26 loss TM-18b exists to prevent.
//
// So the RISING edge stays synchronous (a field taking the keyboard is true
// the instant it says so) and only the FALLING edge waits. A grace, not a
// debounce: an arriving focus CANCELS it, so a hand-over never reaches the
// consumers at all, while a real release still lands within a frame.
//
// ⛔ Not a microtask. Whether RN delivers blur and focus in one JS task or two
// is precisely what LG-72 could not settle from the bench, and a microtask
// only covers the one-task case — the cheaper guess would leave the bug on the
// half of the world it was reported from. 50 ms is longer than any hand-over
// and shorter than a gesture: getting it wrong long costs the resume one extra
// frame of waiting after the athlete really put the keyboard down, and getting
// it wrong short costs the bug back.
const HANDOVER_GRACE_MS = 50;

/** Owner side — called once, in the LoggerScreen body, beside the cursor. */
export function useFieldFocusSignal(): FieldFocusSignal {
  // A SET, not a counter: focus moves between fields as focus-B-then-blur-A as
  // often as the other way round, and a counter that goes negative once never
  // reads true again.
  const held = useRef<Set<string>>(new Set());
  // The grace's mechanics — arm, re-arm, cancel, clear on unmount — come from
  // the shared primitive; only WHEN to arm and what to re-check on fire is
  // this hook's business.
  const release = useCancelableTimeout();
  const [anyFieldFocused, setAnyFieldFocused] = useState(false);

  const onFieldFocus = useCallback(
    (fieldId: string) => {
      held.current.add(fieldId);
      // The field that just took the keyboard cancels the release the field
      // that let go had armed. This is the whole cure.
      release.cancel();
      setAnyFieldFocused(true);
    },
    [release],
  );

  const onFieldBlur = useCallback(
    (fieldId: string) => {
      held.current.delete(fieldId);
      // Another field still holds it — nothing to arm, and nothing changed.
      if (held.current.size > 0) return;
      release.schedule(() => {
        // Re-read rather than trusting the closure: a focus that arrived and
        // cancelled would not be here, but a focus-then-blur pair inside the
        // grace would, and the Set is the only thing that knows.
        if (held.current.size === 0) setAnyFieldFocused(false);
      }, HANDOVER_GRACE_MS);
    },
    [release],
  );

  const releaseAll = useCallback(() => {
    // Immediate, with no grace: the grace exists to survive a hand-over
    // between two fields, and this is not one — it is "the keyboard is gone".
    release.cancel();
    held.current.clear();
    setAnyFieldFocused(false);
  }, [release]);

  return useMemo(
    () => ({ anyFieldFocused, onFieldFocus, onFieldBlur, releaseAll }),
    [anyFieldFocused, onFieldFocus, onFieldBlur, releaseAll],
  );
}

/**
 * Leaf side — one per component; chain `onFocus(id)` / `onBlur(id)` onto each
 * field's own handlers.
 *
 * The id is passed at CALL time, not at hook time, so a panel that renders its
 * cells from a list (the cardio optionals) reports like any other — a hook per
 * cell is not available inside a map, and half a panel reporting is worse than
 * none: the resume would read "nobody is typing" and fire on the one branch
 * that was missed.
 *
 * `fieldId` only has to be unique among the fields mounted AT THE SAME TIME.
 *
 * ⚠️ Two things here are load-bearing, not tidiness:
 *
 *  - **The signal is read through a ref.** Depending on its identity is what
 *    breaks it: the owner rebuilds the signal object when anyFieldFocused
 *    flips, so an effect keyed on it re-runs — and its CLEANUP blurs the field
 *    that just took focus, inside the same flush. The signal then reads false
 *    exactly when it should read true, and nothing downstream ever sees the
 *    keyboard. (Found by the review, reproduced with a harness, and now pinned
 *    by fieldFocusSignal.test.tsx.)
 *  - **The unmount cleanup releases every id this component raised.** A focused
 *    field can be unmounted without ever blurring (the card swaps under it).
 *    Left in the set it would make anyFieldFocused true forever, and the resume
 *    would wait for a blur that can never come.
 */
export function useFieldFocusReporter(): {
  onFocus: (fieldId: string) => void;
  onBlur: (fieldId: string) => void;
} {
  // Tolerate the context's absence: these panels are also rendered outside the
  // provider (isolated tests), and a field that cannot report is a field that
  // simply never blocks the resume.
  const signal = useContext(LoggerContext)?.fieldFocus ?? null;
  const signalRef = useRef<FieldFocusSignal | null>(signal);
  signalRef.current = signal;
  const raised = useRef<Set<string>>(new Set());

  const onFocus = useCallback((fieldId: string) => {
    raised.current.add(fieldId);
    signalRef.current?.onFieldFocus(fieldId);
  }, []);
  const onBlur = useCallback((fieldId: string) => {
    raised.current.delete(fieldId);
    signalRef.current?.onFieldBlur(fieldId);
  }, []);

  useEffect(
    () => () => {
      for (const fieldId of raised.current) {
        signalRef.current?.onFieldBlur(fieldId);
      }
      raised.current.clear();
    },
    [],
  );

  return useMemo(() => ({ onFocus, onBlur }), [onFocus, onBlur]);
}
